'use client';

import * as React from "react";
import { 
  getAuth, 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  type User  // ✅ FIXED: Changed to type-only import
} from "firebase/auth";
import { getApps } from "firebase/app";
import { toastFirebaseError } from "./use-toast";
import { getAuthStabilizationDelay } from "../lib/auth-timing";

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

interface AuthHookReturn extends AuthState {
  signOut: () => Promise<void>;
  refreshToken: () => Promise<void>;
  ensureValidToken: (maxRetries?: number) => Promise<string | null>;
  isInterpreter: boolean;
  isAdmin: boolean;
  userRole: 'user' | 'interpreter' | 'admin' | 'moderator' | null;
}

// Enhanced token validation helper
const ensureValidToken = async (user: User, maxRetries: number = 3): Promise<string | null> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const token = await user.getIdToken(true);
      if (token) {
        console.log(`✅ Token refreshed successfully (attempt ${attempt})`);
        return token;
      }
    } catch (error: any) {
      console.warn(`Token refresh attempt ${attempt} failed:`, error);
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }
  }
  
  console.error('❌ Failed to obtain valid token after retries');
  return null;
};

// ✅ Enhanced profile data loading with Talksie-specific logic
const loadProfileData = async (user: User): Promise<{ role: string | null; isInterpreter: boolean; isAdmin: boolean }> => {
  try {
    console.log(`🔄 Loading Talksie profile data for user: ${user.uid}`);
    
    // Check if user has required custom claims or profile data
    const tokenResult = await user.getIdTokenResult();
    const customClaims = tokenResult.claims;
    
    console.log('📋 User token claims:', {
      hasCustomClaims: Object.keys(customClaims).length > 0,
      emailVerified: customClaims.email_verified,
      authTime: customClaims.auth_time,
      talksieRole: customClaims.talksie_role || null,
      talksieVerified: customClaims.talksie_verified || false,
    });
    
    // Extract Talksie-specific role information
    const role = (customClaims.talksie_role as string) || null;
    const isInterpreter = role === 'interpreter';
    const isAdmin = role === 'admin' || role === 'moderator';
    
    // TODO: Add Firestore profile loading for interpreter details
    // Example for Talksie marketplace:
    // const db = getFirebaseDb();
    // const interpreterRef = doc(db, 'interpreters', user.uid);
    // const interpreterSnap = await getDoc(interpreterRef);
    // 
    // if (isInterpreter && !interpreterSnap.exists()) {
    //   console.log('📝 Interpreter profile not found - user may need to complete signup');
    // }
    
    console.log('✅ Talksie profile data loaded successfully', { role, isInterpreter, isAdmin });
    return { role, isInterpreter, isAdmin };
  } catch (error) {
    console.error('❌ Error loading Talksie profile data:', error);
    // Return default values - don't throw, let auth flow continue
    return { role: null, isInterpreter: false, isAdmin: false };
  }
};

export function useAuth(): AuthHookReturn {
  const [state, setState] = React.useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    isAuthenticated: false,
  });
  
  // ✅ NEW: Talksie-specific user role state
  const [userRole, setUserRole] = React.useState<'user' | 'interpreter' | 'admin' | 'moderator' | null>(null);
  const [isInterpreter, setIsInterpreter] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);

  // Environment-specific delay for Firebase Studio timing fix
  const authDelay = getAuthStabilizationDelay();

  React.useEffect(() => {
    // ✅ FIXED: Properly typed waitForFirebase function
    const waitForFirebase = (): (() => void) | void => {
      if (getApps().length === 0) {
        console.log('⏳ Waiting for Firebase initialization...');
        setTimeout(waitForFirebase, 100);
        return; // ✅ Explicit return for this path
      }

      console.log('✅ Firebase initialized, setting up auth listener');

      try {
        const auth = getAuth();

        // ✅ Enhanced auth state listener with better error handling
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          console.log('🔐 Auth state changed:', currentUser ? `User: ${currentUser.uid}` : 'No user');

          if (!currentUser) {
            setState({
              user: null,
              loading: false,
              error: null,
              isAuthenticated: false,
            });
            // ✅ Clear Talksie-specific state
            setUserRole(null);
            setIsInterpreter(false);
            setIsAdmin(false);
            return;
          }

          try {
            console.log(`✅ Processing Talksie authentication for user: ${currentUser.uid}`);
            
            // ✅ Enhanced user validation
            if (!currentUser.uid) {
              throw new Error('Invalid user data received from Firebase');
            }

            // Wait for authentication state to stabilize (Firebase Studio fix)
            if (authDelay > 0) {
              console.log(`⏳ Waiting ${authDelay}ms for auth state to stabilize...`);
              await new Promise(resolve => setTimeout(resolve, authDelay));
            }

            // Force token refresh to ensure latest credentials
            console.log('🔄 Refreshing authentication token...');
            const token = await ensureValidToken(currentUser);
            
            if (token) {
              // ✅ Load Talksie-specific profile data
              const profileData = await loadProfileData(currentUser);
              
              setState({
                user: currentUser,
                loading: false,
                error: null,
                isAuthenticated: true,
              });
              
              // ✅ Set Talksie-specific role state
              setUserRole(profileData.role as any);
              setIsInterpreter(profileData.isInterpreter);
              setIsAdmin(profileData.isAdmin);

              console.log('✅ Talksie authentication flow completed successfully', {
                role: profileData.role,
                isInterpreter: profileData.isInterpreter,
                isAdmin: profileData.isAdmin,
              });
            } else {
              throw new Error('Failed to obtain valid authentication token');
            }
          } catch (error: any) {
            console.error('❌ Error in Talksie authentication flow:', error);
            
            setState({
              user: null,
              loading: false,
              error: error.message || 'Authentication error occurred',
              isAuthenticated: false,
            });
            
            // ✅ Clear Talksie-specific state on error
            setUserRole(null);
            setIsInterpreter(false);
            setIsAdmin(false);

            // ✅ FIXED: Pass only the error parameter
            toastFirebaseError(error);
          }
        });

        return unsubscribe; // ✅ Return cleanup function
      } catch (error: any) {
        console.error('❌ Failed to initialize auth listener:', error);
        setState({
          user: null,
          loading: false,
          error: error.message,
          isAuthenticated: false,
        });
        return; // ✅ Explicit return for error path
      }
    };

    // ✅ FIXED: Handle the return value properly
    const cleanup = waitForFirebase();
    
    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [authDelay]);

  // ✅ Enhanced sign out function with Talksie state cleanup
  const signOut = React.useCallback(async (): Promise<void> => {
    try {
      if (getApps().length === 0) {
        console.warn('⚠️ Firebase not initialized for sign out');
        return;
      }

      console.log('🚪 Signing out Talksie user...');
      const auth = getAuth();
      
      // Clear state immediately for better UX
      setState(prev => ({ ...prev, loading: true }));
      
      await firebaseSignOut(auth);
      
      setState({
        user: null,
        loading: false,
        error: null,
        isAuthenticated: false,
      });
      
      // ✅ Clear Talksie-specific state
      setUserRole(null);
      setIsInterpreter(false);
      setIsAdmin(false);

      console.log('✅ Talksie sign out completed successfully');
    } catch (error: any) {
      console.error('❌ Sign out error:', error);
      
      // Reset loading state even if sign out fails
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message 
      }));

      // ✅ FIXED: Pass only the error parameter
      toastFirebaseError(error);
    }
  }, []);

  // ✅ Enhanced manual token refresh
  const refreshToken = React.useCallback(async (): Promise<void> => {
    if (!state.user) {
      console.warn('⚠️ No user available for token refresh');
      return;
    }
    
    try {
      console.log('🔄 Manual token refresh initiated...');
      const token = await ensureValidToken(state.user);
      
      if (token) {
        console.log('✅ Manual token refresh successful');
        
        // ✅ Reload profile data after token refresh
        const profileData = await loadProfileData(state.user);
        setUserRole(profileData.role as any);
        setIsInterpreter(profileData.isInterpreter);
        setIsAdmin(profileData.isAdmin);
        
        // Update state to clear any previous errors
        setState(prev => ({ ...prev, error: null }));
      } else {
        throw new Error('Failed to refresh authentication token');
      }
    } catch (error: any) {
      console.error('❌ Manual token refresh failed:', error);
      setState(prev => ({ ...prev, error: error.message }));
      
      // ✅ FIXED: Pass only the error parameter
      toastFirebaseError(error);
    }
  }, [state.user]);

  // ✅ Enhanced token validation wrapper with better error handling
  const ensureValidTokenWrapper = React.useCallback(async (maxRetries: number = 3): Promise<string | null> => {
    if (!state.user) {
      console.warn('⚠️ No user available for token validation');
      return null;
    }
    
    try {
      return await ensureValidToken(state.user, maxRetries);
    } catch (error: any) {
      console.error('❌ Token validation failed:', error);
      setState(prev => ({ ...prev, error: error.message }));
      return null;
    }
  }, [state.user]);

  return {
    ...state,
    signOut,
    refreshToken,
    ensureValidToken: ensureValidTokenWrapper,
    isInterpreter,
    isAdmin,
    userRole,
  };
}
